import { useMutation, useQuery } from '@tanstack/react-query'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/elements/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/elements/dialog'
import { Input } from '@/components/elements/input'
import { getUsers, mergeUsers } from '@/services/user.service'
import { cn, toast } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

/**
 * Merge a duplicate user account into another account (RSCX-351).
 *
 * The admin picks the surviving account; the duplicate's memberships, course
 * history, certificates, and email addresses all move to it, and the duplicate
 * is deactivated. Every email that worked before keeps signing in to the
 * surviving account.
 */
const MergeUserDialog = ({ duplicate, onClose, onMerged }) => {
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [target, setTarget] = useState(null)

  const debounceSearch = useRef(_.debounce((value) => setSearchTerm(value), 400)).current

  // A trailing debounce invocation after the dialog closes would set state on
  // an unmounted component.
  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => debounceSearch.cancel()
  }, [debounceSearch])

  const { data: candidates, isFetching } = useQuery({
    queryKey: ['admin', 'users', 'merge-candidates', searchTerm],
    queryFn: () =>
      getUsers({
        sorting: [{ id: 'last_name', desc: false }],
        pagination: { pageIndex: 0, pageSize: 10 },
        searchTerm,
        filters: {},
      }),
    enabled: searchTerm.length >= 2,
  })

  const { mutate: merge, isPending: merging } = useMutation({
    mutationFn: () => mergeUsers({ primaryId: target.id, duplicateId: duplicate.id }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: () => {
      toast(`${formatUserName(duplicate)} merged into ${formatUserName(target)}`, 'success')
      onMerged()
    },
  })

  const results = (candidates?.results ?? []).filter(
    (candidate) => candidate.id !== duplicate.id && !candidate.mergedInto,
  )

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Merge User</DialogTitle>
          <DialogDescription>
            Merge <span className="font-medium">{formatUserName(duplicate)}</span> (
            {duplicate.email}) into another account. Their memberships, course history,
            certificates, and email addresses move to the account you choose, and this account is
            deactivated. They&apos;ll be able to sign in to the surviving account with any of their
            email addresses.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <Input
            placeholder="Search for the account to keep"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setTarget(null)
              debounceSearch(e.target.value)
            }}
          />

          {searchTerm.length >= 2 && (
            <ul className="max-h-56 divide-y overflow-y-auto rounded border">
              {results.map((candidate) => (
                <li key={candidate.id}>
                  <button
                    type="button"
                    className={cn(
                      'hover:bg-accent flex w-full flex-col items-start px-3 py-2 text-left',
                      target?.id === candidate.id && 'bg-accent',
                    )}
                    onClick={() => setTarget(candidate)}
                  >
                    <span className="text-sm font-medium">{formatUserName(candidate)}</span>
                    <span className="text-muted-foreground text-xs">{candidate.email}</span>
                  </button>
                </li>
              ))}
              {!isFetching && results.length === 0 && (
                <li className="text-muted-foreground px-3 py-2 text-sm">No matching users.</li>
              )}
            </ul>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={merging}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={!target}
            loading={merging}
            onClick={() => merge()}
          >
            {target ? `Merge into ${formatUserName(target)}` : 'Merge'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

MergeUserDialog.propTypes = {
  duplicate: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onMerged: PropTypes.func.isRequired,
}

export default MergeUserDialog
